import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Image,
  Upload,
  Trash2,
  Edit,
  Instagram,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Plus,
  ExternalLink,
} from 'lucide-react';
import api from '../../services/api';

const HeroImagesManagement = () => {
  const [images, setImages] = useState([]);
  const [instagramSettings, setInstagramSettings] = useState({
    enabled: false,
    instagram_handle: '',
    display_count: 4,
    show_on_homepage: true,
    show_on_players_page: true
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingInsta, setSavingInsta] = useState(false);
  
  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadPreview, setUploadPreview] = useState('');
  
  // Edit dialog
  const [editingImage, setEditingImage] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // Delete confirmation dialog
  const [deleteConfirmImage, setDeleteConfirmImage] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [imagesRes, instaRes] = await Promise.all([
        api.get('/hero-images?active_only=false'),
        api.get('/hero-images/instagram-settings')
      ]);
      setImages(imagesRes.data || []);
      setInstagramSettings(instaRes.data || {
        enabled: false,
        instagram_handle: '',
        display_count: 4,
        show_on_homepage: true,
        show_on_players_page: true
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploadFile(file);
    setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select an image');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle || uploadFile.name);
      formData.append('description', uploadDescription);
      formData.append('display_order', images.length);
      
      await api.post('/hero-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Image uploaded successfully');
      setShowUploadDialog(false);
      resetUploadForm();
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadPreview('');
  };

  const handleToggleActive = async (image) => {
    try {
      await api.put(`/hero-images/${image.id}`, {
        is_active: !image.is_active
      });
      toast.success(`Image ${image.is_active ? 'hidden' : 'shown'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  const handleDelete = async (image) => {
    // Open confirmation dialog instead of window.confirm
    setDeleteConfirmImage(image);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmImage) return;
    
    setDeleting(true);
    try {
      await api.delete(`/hero-images/${deleteConfirmImage.id}`);
      toast.success('Image deleted');
      setDeleteConfirmImage(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = (image) => {
    setEditingImage(image);
    setEditTitle(image.title || '');
    setEditDescription(image.description || '');
  };

  const handleEditSave = async () => {
    if (!editingImage) return;
    
    try {
      await api.put(`/hero-images/${editingImage.id}`, {
        title: editTitle,
        description: editDescription
      });
      toast.success('Image updated');
      setEditingImage(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  const handleSaveInstagramSettings = async () => {
    setSavingInsta(true);
    try {
      await api.put('/hero-images/instagram-settings', instagramSettings);
      toast.success('Instagram settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingInsta(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Hero Images">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Hero Images & Gallery">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Hero Images & Gallery</h1>
            <p className="text-slate-600">Manage images shown on the homepage and Our Players page</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-image-btn">
            <Plus className="w-4 h-4 mr-2" />
            Upload Image
          </Button>
          </div>
        </div>

        {/* Images Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              Gallery Images
            </CardTitle>
            <CardDescription>
              {images.filter(i => i.is_active).length} active images, {images.filter(i => !i.is_active).length} hidden
            </CardDescription>
          </CardHeader>
          <CardContent>
            {images.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Image className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">No images uploaded yet</p>
                <p className="text-sm mt-1">Upload images to display on the homepage gallery</p>
                <Button 
                  className="mt-4" 
                  onClick={() => setShowUploadDialog(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload First Image
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {images.map((image, index) => (
                  <div 
                    key={image.id}
                    className={`relative rounded-lg overflow-hidden border ${
                      image.is_active ? 'border-slate-200' : 'border-slate-300 opacity-60'
                    }`}
                  >
                    <div className="aspect-video bg-slate-100">
                      <img
                        src={image.file_url}
                        alt={image.title || 'Hero image'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Status Badge */}
                    <div className="absolute top-2 left-2">
                      <Badge className={image.is_active ? 'bg-green-500' : 'bg-slate-500'}>
                        {image.is_active ? 'Active' : 'Hidden'}
                      </Badge>
                    </div>
                    
                    {/* Order Badge */}
                    <div className="absolute top-2 right-2">
                      <Badge variant="outline" className="bg-white/90">
                        #{index + 1}
                      </Badge>
                    </div>
                    
                    {/* Info & Actions */}
                    <div className="p-3 bg-white">
                      <h4 className="font-medium text-sm truncate">{image.title || 'Untitled'}</h4>
                      {image.description && (
                        <p className="text-xs text-slate-500 truncate mt-1">{image.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(image)}
                          className="flex-1"
                        >
                          {image.is_active ? (
                            <>
                              <EyeOff className="w-3 h-3 mr-1" />
                              Hide
                            </>
                          ) : (
                            <>
                              <Eye className="w-3 h-3 mr-1" />
                              Show
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(image)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(image)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instagram Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Instagram className="w-5 h-5 text-pink-500" />
              Instagram Feed Settings
            </CardTitle>
            <CardDescription>
              Configure Instagram feed display on the homepage and Our Players page
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Instagram Feed</Label>
                <p className="text-sm text-slate-500 mt-1">Show Instagram posts on public pages</p>
              </div>
              <Switch
                checked={instagramSettings.enabled}
                onCheckedChange={(checked) => setInstagramSettings(prev => ({ ...prev, enabled: checked }))}
                data-testid="instagram-enabled-switch"
              />
            </div>
            
            {instagramSettings.enabled && (
              <div className="space-y-4 pl-4 border-l-2 border-pink-200">
                <div>
                  <Label>Instagram Handle</Label>
                  <div className="flex items-center mt-1">
                    <span className="px-3 py-2 bg-slate-100 border border-r-0 rounded-l-md text-slate-500">@</span>
                    <Input
                      value={instagramSettings.instagram_handle}
                      onChange={(e) => setInstagramSettings(prev => ({ ...prev, instagram_handle: e.target.value }))}
                      placeholder="delhicapitalsacademynj"
                      className="rounded-l-none"
                      data-testid="instagram-handle-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Number of Posts to Display</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={instagramSettings.display_count}
                    onChange={(e) => setInstagramSettings(prev => ({ ...prev, display_count: parseInt(e.target.value) || 4 }))}
                    className="w-24 mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">1-10 posts (recommended: 4-5)</p>
                </div>
                
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={instagramSettings.show_on_homepage}
                      onCheckedChange={(checked) => setInstagramSettings(prev => ({ ...prev, show_on_homepage: checked }))}
                    />
                    <span className="text-sm">Show on Homepage</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={instagramSettings.show_on_players_page}
                      onCheckedChange={(checked) => setInstagramSettings(prev => ({ ...prev, show_on_players_page: checked }))}
                    />
                    <span className="text-sm">Show on Our Players Page</span>
                  </label>
                </div>
                
                {instagramSettings.instagram_handle && (
                  <a
                    href={`https://instagram.com/${instagramSettings.instagram_handle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-pink-600 hover:text-pink-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Preview @{instagramSettings.instagram_handle}
                  </a>
                )}
              </div>
            )}
            
            <div className="pt-4 border-t">
              <Button 
                onClick={handleSaveInstagramSettings}
                disabled={savingInsta}
                data-testid="save-instagram-settings-btn"
              >
                {savingInsta ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Instagram Settings
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!open) resetUploadForm(); setShowUploadDialog(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Hero Image</DialogTitle>
            <DialogDescription>
              Add a new image to the homepage gallery
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Input */}
            <div>
              <Label>Image File</Label>
              <div className="mt-1">
                {uploadPreview ? (
                  <div className="relative rounded-lg overflow-hidden">
                    <img
                      src={uploadPreview}
                      alt="Preview"
                      className="w-full aspect-video object-cover"
                    />
                    <button
                      onClick={resetUploadForm}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click to upload or drag and drop</span>
                    <span className="text-xs text-slate-400 mt-1">PNG, JPG, WebP, GIF up to 10MB</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="image-file-input"
                    />
                  </label>
                )}
              </div>
            </div>
            
            <div>
              <Label>Title</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="e.g., Summer Tournament 2026"
                className="mt-1"
                data-testid="image-title-input"
              />
            </div>
            
            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description of the image..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading}>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingImage} onOpenChange={(open) => !open && setEditingImage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Image</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editingImage && (
              <div className="rounded-lg overflow-hidden">
                <img
                  src={editingImage.file_url}
                  alt={editingImage.title}
                  className="w-full aspect-video object-cover"
                />
              </div>
            )}
            
            <div>
              <Label>Title</Label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingImage(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmImage} onOpenChange={(open) => !open && setDeleteConfirmImage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Image</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirmImage?.title || 'this image'}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deleteConfirmImage && (
            <div className="py-4">
              <div className="rounded-lg overflow-hidden border">
                <img
                  src={deleteConfirmImage.file_url}
                  alt={deleteConfirmImage.title}
                  className="w-full aspect-video object-cover"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmImage(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Image
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default HeroImagesManagement;
